"use client"

import * as React from 'react'
import Link from 'next/link'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@open-mercato/ui/primitives/button'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import {
  DriverErrorState,
  DriverInlineMessage,
  DriverLoadingState,
  DriverPanel,
  DriverPwaShell,
  DriverStatusBadge,
  fetchDriverActiveOrder,
  fetchDriverProfile,
  fetchDriverQrArtifact,
  formatDriverDateTime,
  formatDriverFileSize,
  type DriverActiveOrder,
} from '../../../lib/driverPwaUi'

type NoticeState = {
  tone: 'info' | 'success' | 'error'
  text: string
} | null

type StatusUpdateResponse = {
  ok?: boolean
  queued?: boolean
  active?: DriverActiveOrder | null
  error?: string
}

type ManualLocationResponse = {
  ok?: boolean
  queued?: boolean
  error?: string
}

type PhotoUploadResponse = {
  ok?: boolean
  item?: {
    id: string
  }
  error?: string
}

function extractMessage(payload: unknown, fallback: string): string {
  if (!payload) return fallback
  if (typeof payload === 'string') return payload
  if (Array.isArray(payload)) {
    for (const item of payload) {
      const nested = extractMessage(item, '')
      if (nested) return nested
    }
    return fallback
  }
  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    return extractMessage(record.error, '') || extractMessage(record.message, '') || fallback
  }
  return fallback
}

export default function DriverActiveOrderPage() {
  const queryClient = useQueryClient()
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  const [notice, setNotice] = React.useState<NoticeState>(null)
  const [statusPending, setStatusPending] = React.useState<string | null>(null)
  const [gpsPending, setGpsPending] = React.useState(false)
  const [photoPending, setPhotoPending] = React.useState(false)

  const profileQuery = useQuery({
    queryKey: ['driver-pwa', 'profile'],
    queryFn: fetchDriverProfile,
    retry: false,
  })
  const activeQuery = useQuery({
    queryKey: ['driver-pwa', 'active'],
    queryFn: fetchDriverActiveOrder,
    retry: false,
    refetchInterval: 15000,
  })
  const qrQuery = useQuery({
    queryKey: ['driver-pwa', 'active', 'qr'],
    queryFn: fetchDriverQrArtifact,
    retry: false,
    enabled: Boolean(activeQuery.data?.ecmr),
    refetchInterval: 30000,
  })

  const refreshActiveData = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['driver-pwa', 'active'] })
    await queryClient.invalidateQueries({ queryKey: ['driver-pwa', 'orders'] })
    await queryClient.invalidateQueries({ queryKey: ['driver-pwa', 'active', 'qr'] })
  }, [queryClient])

  const activeOrder = activeQuery.data ?? null

  const handleStatusUpdate = React.useCallback(async (nextStatus: string) => {
    if (!activeOrder) return
    setStatusPending(nextStatus)
    setNotice(null)
    try {
      const call = await apiCall<StatusUpdateResponse>('/api/road/driver/active/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dispatchId: activeOrder.id,
          version: activeOrder.version,
          status: nextStatus,
        }),
      })
      if (!call.ok) {
        setNotice({ tone: 'error', text: extractMessage(call.result, 'Failed to update status.') })
        return
      }
      if (call.status === 202 || call.result?.queued) {
        setNotice({ tone: 'info', text: 'Status update queued offline. It will be replayed automatically when connectivity returns.' })
        return
      }
      setNotice({ tone: 'success', text: `Status updated to ${nextStatus.replaceAll('_', ' ')}.` })
      await refreshActiveData()
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to update status.' })
    } finally {
      setStatusPending(null)
    }
  }, [activeOrder, refreshActiveData])

  const handleManualGps = React.useCallback(async () => {
    if (!activeOrder || gpsPending) return
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setNotice({ tone: 'error', text: 'Geolocation is not available on this device.' })
      return
    }
    setGpsPending(true)
    setNotice(null)
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const call = await apiCall<ManualLocationResponse>('/api/road/driver/active/manual-location', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              dispatchId: activeOrder.id,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracyM: position.coords.accuracy ?? null,
              altitudeM: position.coords.altitude ?? null,
              headingDeg: position.coords.heading ?? null,
              speedKmh: position.coords.speed != null ? Number(position.coords.speed) * 3.6 : null,
              recordedAt: new Date().toISOString(),
            }),
          })
          if (!call.ok) {
            setNotice({ tone: 'error', text: extractMessage(call.result, 'Failed to send manual location.') })
            return
          }
          if (call.status === 202 || call.result?.queued) {
            setNotice({ tone: 'info', text: 'Manual location queued offline. It will sync when the device reconnects.' })
            return
          }
          setNotice({ tone: 'success', text: 'Manual location sent.' })
        } catch (error) {
          setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to send manual location.' })
        } finally {
          setGpsPending(false)
        }
      },
      (error) => {
        setNotice({ tone: 'error', text: error.message || 'Unable to read device location.' })
        setGpsPending(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0,
      },
    )
  }, [activeOrder, gpsPending])

  const handlePhotoUpload = React.useCallback(async (files: FileList | null) => {
    if (!activeOrder || !files?.length) return
    const file = files[0]
    setPhotoPending(true)
    setNotice(null)
    try {
      const form = new FormData()
      form.set('file', file)
      const call = await apiCall<PhotoUploadResponse>('/api/road/driver/active/photos', {
        method: 'POST',
        body: form,
      })
      if (!call.ok) {
        setNotice({ tone: 'error', text: extractMessage(call.result, 'Failed to upload photo.') })
        return
      }
      setNotice({ tone: 'success', text: 'Photo uploaded.' })
      await refreshActiveData()
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to upload photo.' })
    } finally {
      setPhotoPending(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [activeOrder, refreshActiveData])

  if ((profileQuery.isLoading && !profileQuery.data) || (activeQuery.isLoading && activeQuery.data === undefined)) {
    return <DriverLoadingState />
  }

  if (profileQuery.isError) {
    return <DriverErrorState title="Driver PWA is not available" description={profileQuery.error instanceof Error ? profileQuery.error.message : 'This account is not linked to an active driver profile.'} />
  }

  if (activeQuery.isError) {
    return <DriverErrorState title="Active order is unavailable" description={activeQuery.error instanceof Error ? activeQuery.error.message : 'Failed to load the active order.'} />
  }

  return (
    <DriverPwaShell
      title="Active Order"
      subtitle="Use the status buttons for the live flow, send manual GPS when needed, capture proof photos and show the eCMR QR during inspections."
      profile={profileQuery.data ?? null}
    >
      {notice ? <DriverInlineMessage tone={notice.tone} text={notice.text} /> : null}

      {!activeOrder ? (
        <DriverPanel eyebrow="Waiting" title="No active order">
          <div className="space-y-4">
            <p className="text-sm leading-6 text-slate-300">
              No active dispatch is currently assigned to this driver account.
            </p>
            <Button asChild className="w-full rounded-2xl bg-sky-500 text-slate-950 hover:bg-sky-400">
              <Link href="/driver/orders">Open My Orders</Link>
            </Button>
          </div>
        </DriverPanel>
      ) : (
        <>
          <DriverPanel
            eyebrow="Current dispatch"
            title={activeOrder.orderNumber}
            action={<DriverStatusBadge status={activeOrder.status} />}
          >
            <div className="space-y-4">
              <div className="rounded-[24px] bg-white/6 px-4 py-4">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Route</div>
                <div className="mt-2 text-base font-medium text-slate-50">
                  {activeOrder.originLabel ?? 'Origin pending'} to {activeOrder.destinationLabel ?? 'Destination pending'}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-[22px] bg-slate-950/45 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Pickup</div>
                  <div className="mt-2 font-medium">{formatDriverDateTime(activeOrder.plannedPickupAt)}</div>
                </div>
                <div className="rounded-[22px] bg-slate-950/45 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Delivery</div>
                  <div className="mt-2 font-medium">{formatDriverDateTime(activeOrder.plannedDeliveryAt)}</div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-[22px] border border-white/8 bg-white/5 px-4 py-3 text-sm text-slate-200">
                <span>Vehicle</span>
                <span className="font-medium">{activeOrder.vehiclePlate ?? 'Not assigned'}</span>
              </div>

              {activeOrder.dispatcherNotes ? (
                <div className="rounded-[22px] border border-amber-400/18 bg-amber-500/10 px-4 py-4 text-sm leading-6 text-amber-50">
                  {activeOrder.dispatcherNotes}
                </div>
              ) : null}
            </div>
          </DriverPanel>

          <DriverPanel eyebrow="Workflow" title="Status Actions">
            <div className="grid gap-3">
              {activeOrder.statusActions.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/12 px-4 py-4 text-sm text-slate-300">
                  No further workflow actions are available for this order.
                </div>
              ) : null}
              {activeOrder.statusActions.map((action) => (
                <Button
                  key={action.status}
                  type="button"
                  disabled={statusPending !== null}
                  className="h-14 rounded-[22px] bg-sky-500 text-base font-semibold text-slate-950 hover:bg-sky-400 disabled:bg-slate-700 disabled:text-slate-300"
                  onClick={() => { void handleStatusUpdate(action.status) }}
                >
                  {statusPending === action.status ? 'Updating…' : action.label}
                </Button>
              ))}
            </div>
          </DriverPanel>

          <DriverPanel eyebrow="Manual tools" title="GPS and Photos">
            <div className="grid gap-3">
              <Button
                type="button"
                variant="outline"
                className="h-14 rounded-[22px] border-sky-300/30 bg-sky-500/10 text-base text-sky-50 hover:bg-sky-500/20"
                disabled={gpsPending}
                onClick={() => { void handleManualGps() }}
              >
                {gpsPending ? 'Sending location…' : 'Send Manual GPS'}
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(event) => { void handlePhotoUpload(event.target.files) }}
              />
              <Button
                type="button"
                variant="outline"
                className="h-14 rounded-[22px] border-amber-300/30 bg-amber-500/10 text-base text-amber-50 hover:bg-amber-500/20"
                disabled={photoPending}
                onClick={() => fileInputRef.current?.click()}
              >
                {photoPending ? 'Uploading photo…' : 'Capture / Upload Photo'}
              </Button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              {activeOrder.photos.map((photo) => (
                <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer" className="group overflow-hidden rounded-[22px] border border-white/8 bg-white/5">
                  <div className="aspect-square overflow-hidden bg-slate-900">
                    <img src={photo.thumbnailUrl} alt={photo.fileName} className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]" />
                  </div>
                  <div className="px-3 py-3">
                    <div className="truncate text-sm font-medium text-slate-100">{photo.fileName}</div>
                    <div className="mt-1 text-xs text-slate-400">{formatDriverFileSize(photo.fileSize)}</div>
                  </div>
                </a>
              ))}
              {activeOrder.photos.length === 0 ? (
                <div className="col-span-2 rounded-[22px] border border-dashed border-white/12 px-4 py-5 text-sm text-slate-300">
                  No proof photos captured for this order yet.
                </div>
              ) : null}
            </div>
          </DriverPanel>

          <DriverPanel eyebrow="Inspection" title="eCMR QR">
            {!activeOrder.ecmr ? (
              <div className="rounded-[22px] border border-dashed border-white/12 px-4 py-5 text-sm text-slate-300">
                No eCMR document is linked to the active dispatch.
              </div>
            ) : qrQuery.isError ? (
              <div className="rounded-[22px] border border-rose-400/20 bg-rose-500/8 px-4 py-5 text-sm text-rose-100">
                {qrQuery.error instanceof Error ? qrQuery.error.message : 'Failed to load the eCMR QR.'}
              </div>
            ) : qrQuery.isLoading || !qrQuery.data ? (
              <div className="rounded-[22px] border border-white/10 bg-white/5 px-4 py-8 text-center text-sm text-slate-300">
                Loading eCMR QR…
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-[26px] bg-white p-4 text-slate-950" dangerouslySetInnerHTML={{ __html: qrQuery.data.svg }} />
                <div className="rounded-[22px] bg-white/6 px-4 py-4 text-sm text-slate-200">
                  <div className="font-medium">{qrQuery.data.documentNumber}</div>
                  <div className="mt-1 text-slate-400">Seal hash: {qrQuery.data.sealHash}</div>
                </div>
              </div>
            )}
          </DriverPanel>
        </>
      )}
    </DriverPwaShell>
  )
}
